type Overrides = Record<string, Record<string, string>>

import { initFormatters } from './formatters'
import type {
  Formatters,
  Locales,
  TranslationFunctions,
  Translations,
} from './i18n-types'
import { loadedFormatters, loadedLocales } from './i18n-util'

import {
  type LocaleTranslationFunctions,
  i18n as initI18n,
} from 'typesafe-i18n'
import de from './de'
import en from './en'

const localeTranslations = {
  de,
  en,
}

const loadFormatters = (locale: Locales): void => {
  loadedFormatters[locale] = initFormatters(locale)
}

type TreeNode = {
  [key: string]: string | TreeNode
}

const deepOverride = <T extends TreeNode>(
  object: T,
  overrides: Overrides,
  locale: string
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: this is a complex function that processes nested objects and strings
): T => {
  const result = { ...object } as T

  // traverse the tree, when value is a string, check if it has an override
  for (const [key, value] of Object.entries(result)) {
    if (typeof value === 'string') {
      let modifiedValue = value

      // Find all matching override keys for this value
      const matchingOverrides = Object.keys(overrides).filter((overrideKey) =>
        value.toLowerCase().includes(overrideKey.toLowerCase())
      )

      // Sort by length (longest first) to handle overlapping matches properly
      const sortedOverrides = matchingOverrides.sort(
        (a, b) => b.length - a.length
      )

      // Apply all matching overrides
      for (const overrideKey of sortedOverrides) {
        const keyWithLowercase = overrideKey.toLowerCase()

        // Get the override values
        const overrideValues =
          overrideKey in overrides
            ? overrides[overrideKey as keyof Overrides]
            : keyWithLowercase in overrides
              ? overrides[keyWithLowercase as keyof Overrides]
              : undefined

        const overrideValue = overrideValues?.[locale]

        if (overrideValue) {
          // Check if the original text contains the exact case version
          const isCapitalized = modifiedValue.includes(overrideKey)
          const hasLowercase = modifiedValue
            .toLowerCase()
            .includes(keyWithLowercase)

          if (isCapitalized) {
            const newValue =
              overrideValue.charAt(0).toUpperCase() + overrideValue.slice(1)
            modifiedValue = modifiedValue.replaceAll(overrideKey, newValue)
          }

          if (hasLowercase && !isCapitalized) {
            const newValue = overrideValue.toLowerCase()
            // Use case-insensitive replace but preserve original case of surrounding text
            const regex = new RegExp(
              keyWithLowercase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
              'gi'
            )
            modifiedValue = modifiedValue.replace(regex, newValue)
          }
        }
      }
      // Update the result with the modified value
      ;(result as Record<string, string | TreeNode>)[key] = modifiedValue
    } else if (typeof value === 'object' && value !== null) {
      // Recursively process nested objects
      ;(result as Record<string, string | TreeNode>)[key] = deepOverride(
        value,
        overrides,
        locale
      )
    }
  }

  return result
}

const overrideTranslations = (
  translationData: typeof de | typeof en,
  overrides: Overrides,
  locale: string
) => {
  const cloned = structuredClone(translationData)
  return deepOverride(cloned, overrides, locale)
}

const defaultOverrides: Overrides = {
  __Action__: { de: 'Action', en: 'Action' },
  __Actions__: { de: 'Actions', en: 'Actions' },
  __Deliverable__: { de: 'Deliverable', en: 'Deliverable' },
  __Deliverables__: { de: 'Deliverables', en: 'Deliverables' },
  __Target__: { de: 'Target', en: 'Target' },
  __Targets__: { de: 'Targets', en: 'Targets' },
  __Intent__: { de: 'Intent', en: 'Intent' },
  __Intents__: { de: 'Intents', en: 'Intents' },
  '__Higher Intent__': { de: 'Higher Intent', en: 'Higher Intent' },
  '__Higher Intents__': { de: 'Higher Intents', en: 'Higher Intents' },
  '__Action Achievement__': {
    de: 'Action Achievement',
    en: 'Action Achievement',
  },
  '__Action Achievements__': {
    de: 'Action Achievements',
    en: 'Action Achievements',
  },
}

const getOverridenLocaleData = (locale: string, overrides: Overrides) => {
  const typedLocale = locale as Locales
  const translationData = localeTranslations[typedLocale]

  if (!translationData) {
    throw new Error(`Locale ${locale} not found`)
  }

  const appliedOverrides = {
    ...defaultOverrides,
    ...overrides,
  }

  return overrideTranslations(
    translationData,
    appliedOverrides,
    locale
  ) as unknown as Translations
}

export const loadLocaleWithOverrides = (
  locale: string,
  overrides: Overrides
) => {
  const typedLocale = locale as Locales
  const overriddenTranslations = getOverridenLocaleData(typedLocale, overrides)

  loadedLocales[typedLocale] = overriddenTranslations
  loadFormatters(typedLocale)

  return overriddenTranslations
}

export const getTranslatorWithOverrides = <Lang extends Locales>(
  locale: Lang,
  overrides: Overrides
): LocaleTranslationFunctions<
  Lang,
  Translations,
  TranslationFunctions
>[Lang] => {
  const i18n = initI18n<Lang, Translations, TranslationFunctions, Formatters>(
    {
      [locale]: getOverridenLocaleData(locale, overrides),
    } as Record<Lang, Translations>,
    loadedFormatters
  )

  return i18n[locale]
}
